import { Box } from "@mui/material";
import { batch, useSignal } from "@preact/signals-react";
import { mapValues } from "lodash-es";
import { executeAction } from "../../library/actions";
import { publishUserAction } from "../../library/analytics";
import { getLocale, setupLocalState } from "../../library/dataService";
import { useFinApp } from "../../library/finapp";
import { log } from "../../library/logger";
import { INGFinAppProps } from "../../library/NGFieldExtensions";
import { escapeAttribute, getClassName, getLanguageCode, getTestId, getsxObject } from "../../library/utils";

const tag = "NGFinApp";

const alertClickAction = (configId: string, actionName: "onLinkAlert" | "onLinkClick" | "onSearch") => ({
  Trigger: "onFinAppAction",
  CommandSet: {
    FirstCommandId: "1",
    ExecuteCommandsInParallel: false,
    Commands: [
      {
        Id: "1",
        Instruction: {
          Name: "TriggerAction",
        },
        Parameters: [
          {
            Name: "Trigger",
            Value: actionName,
          },
          {
            Name: "TargetId",
            Value: configId,
          },
        ],
      },
    ],
  },
});

export default function NGFinApp({ config, context }: INGFinAppProps) {
  const local = setupLocalState(
    config,
    {
      Visible: useSignal(config.Visible ?? true),
      Classes: useSignal(config.Classes ?? ""),
      Style: useSignal(config.Style ?? {}),
      Type: useSignal(config.Type ?? "news"),
      Symbol: useSignal(config.Symbol),
      Exchange: useSignal(config.Exchange),
      AppConfigId: useSignal(config.AppConfigId),
      Elements: useSignal(config.Elements),
      XID: useSignal(config.XID),
      XIDs: useSignal(config.XIDs),
      HideElements: useSignal(config.HideElements),
      HideLoader: useSignal(config.HideLoader ?? false),
      View: useSignal(config.View),
      EventCount: useSignal(config.EventCount),
      ModelId: useSignal(config.ModelId),
      LimitColumns: useSignal(config.LimitColumns),
      ShowLinks: useSignal(config.ShowLinks),
      RankingType: useSignal(config.RankingType),
      ShouldShowConfiguration: useSignal(config.ShouldShowConfiguration ?? false),
      DisableChartDrag: useSignal(config.DisableChartDrag ?? false),
      AllowPanZoom: useSignal(config.AllowPanZoom ?? false),
      AutoPlay: useSignal(config.AutoPlay ?? false),
      UserPrefs: useSignal(config.UserPrefs ?? {}),
      Indices: useSignal(config.Indices ?? []),
      MarketIdentifier: useSignal(config.MarketIdentifier ?? undefined),
      ShowInternational: useSignal(config.ShowInternational),
      SelectedCountry: useSignal(config.SelectedCountry),
      ComparisonList: useSignal(config.ComparisonList ?? []),
      HideError: useSignal(config.HideError ?? false),
      CardsBelowCharts: useSignal(config.CardsBelowCharts ?? false),
      SelectedRange: useSignal(config.SelectedRange ?? []),
      HideSmarttext: useSignal(config.HideSmarttext ?? false),
      Props: useSignal(config.Props ?? {}),
      DisplayNameInCard: useSignal(config.DisplayNameInCard),
      ShowExchange: useSignal(config.ShowExchange),
      ShowCurrency: useSignal(config.ShowCurrency),
      ShouldScroll: useSignal(config.ShouldScroll),
      MarketCap: useSignal(config.MarketCap ?? ""),
    },
    context
  );

  const onSearch = (data: any, componentId?: string) => {
    batch(() => {
      local.Symbol.value = data.symbol;
      local.Exchange.value = data.exchange.code;
      local.XID.value = data?.xid?.code || data?.xids?.venue;
    });
    if (componentId) executeAction(alertClickAction(componentId, "onSearch"), null, data, context);
  };

  const onLinkSymbolClick = (data: object, componentId: string) => {
    log.info(tag, `onLinkClick for component with Id = ${componentId} with data:`, { data });
    executeAction(alertClickAction(componentId, "onLinkClick"), null, data, context);
  };

  const onLinkAlertClick = (data, componentId) => {
    log.info(tag, `onLinkAlert for component with Id = ${componentId} with data:`, { data });
    executeAction(alertClickAction(componentId, "onLinkAlert"), null, data, context);
  };

  const onAnalytics = (data, e) => {
    publishUserAction(config, context, data.EventName, e, data);
  };

  useFinApp(onSearch, onLinkSymbolClick, onLinkAlertClick, onAnalytics);

  const ComponentTag = `fwc-${local.Type.value}` || "div";

  return (
    <>
      {local.Visible.value && (
        <Box
          data-testid={getTestId(config)}
          data-type={config.__typename}
          sx={getsxObject(local.Style.value)}
          className={getClassName(local.Classes)}
        >
          <ComponentTag
            className="fwc-app"
            {...mapValues(local.Props.value || {}, (v) =>
              typeof v === "object" && v !== null ? escapeAttribute(encodeURI(JSON.stringify(v))) : escapeAttribute(v)
            )}
            xid={escapeAttribute(local.XID.value ?? "")}
            xids={escapeAttribute(local.XIDs.value ?? "")}
            symbol={escapeAttribute(local.Symbol.value ?? "")}
            userPrefs={escapeAttribute(encodeURI(JSON.stringify(local.UserPrefs.value)))}
            exchange={escapeAttribute(local.Exchange.value ?? "")}
            hideElements={escapeAttribute(local.HideElements.value ?? "")}
            hideLoader={local.HideLoader.value}
            indices={escapeAttribute(encodeURI(JSON.stringify(local.Indices.value)))}
            marketIdentifier={escapeAttribute(local.MarketIdentifier.value ?? "")}
            lang={getLanguageCode(getLocale())}
            criteria={escapeAttribute(
              encodeURI(
                JSON.stringify({
                  appConfigId: local.AppConfigId.value,
                  elements: local.Elements.value,
                  xid: local.XID.value,
                  xids: local.XIDs.value,
                })
              )
            )}
            {...(local.MarketCap.value ? { marketCap: escapeAttribute(local.MarketCap.value) } : {})}
            {...(local.ModelId.value ? { modelId: escapeAttribute(local.ModelId.value) } : {})}
            {...(local.View.value ? { view: escapeAttribute(local.View.value) } : {})}
            {...(local.EventCount.value ? { eventcount: escapeAttribute(local.EventCount.value) } : {})}
            {...(local.LimitColumns.value == true ? { limitColumns: true } : {})}
            {...(local.DisableChartDrag.value == true ? { disableChartDrag: true } : {})}
            {...(local.AllowPanZoom.value == true ? { allowPanZoom: true } : {})}
            {...(local.AutoPlay.value == true ? { autoplay: true } : {})}
            {...(local.ShowLinks.value == true ? { showLinks: true } : {})}
            {...(local.DisplayNameInCard.value == true ? { displayNameInCard: true } : {})}
            {...(local.ShowExchange.value == true ? { showExchange: true } : {})}
            {...(local.ShowCurrency.value == true ? { showCurrency: true } : {})}
            {...(local.ShouldScroll.value == true ? { shouldScroll: true } : {})}
            {...(local.RankingType.value ? { rankingType: escapeAttribute(local.RankingType.value) } : {})}
            {...(local.ShouldShowConfiguration.value
              ? { shouldShowConfiguration: local.ShouldShowConfiguration.value }
              : {})}
            {...(local.ShowInternational.value ? { showInternational: local.ShowInternational.value } : {})}
            {...(local.SelectedCountry.value ? { selectedCountry: escapeAttribute(local.SelectedCountry.value) } : {})}
            {...(local.ComparisonList.value
              ? { comparisonList: escapeAttribute(encodeURI(JSON.stringify(local.ComparisonList.value))) }
              : {})}
            {...(local.HideError.value == true ? { hideError: true } : {})}
            {...(local.CardsBelowCharts.value == true ? { cardsBelowCharts: true } : {})}
            {...(local.HideSmarttext.value == true ? { hideSmarttext: true } : {})}
            {...(local.SelectedRange.value
              ? { selectedRange: escapeAttribute(encodeURI(JSON.stringify(local.SelectedRange.value))) }
              : {})}
          ></ComponentTag>
        </Box>
      )}
    </>
  );
}
